
import { Edit2, Trash2, Phone, MapPin, Eye } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { type Product } from "@/components/services/products/types";
import { useNavigate } from "react-router-dom";

interface ProductCardProps {
  product: Product;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
}

export const ProductCard = ({ product, onEdit, onDelete }: ProductCardProps) => {
  const navigate = useNavigate();
  
  const handleAddressClick = () => {
    if (!product.address) return;
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(product.address)}`;
    window.open(mapsUrl, '_blank');
  };

  const handleViewProduct = () => {
    navigate(`/services/products/${product.id}`);
  };

  // Helper function to format product condition
  const formatCondition = (condition: string) => {
    switch (condition) {
      case "new": return "Neuf";
      case "like-new": return "Comme neuf";
      case "good": return "Bon état";
      case "used": return "Utilisé";
      default: return condition;
    }
  };

  // Function to get currency symbol
  const getCurrencySymbol = (currencyCode: string = 'EUR') => {
    switch (currencyCode) {
      case "EUR": return "€";
      case "USD": return "$";
      case "HTG": return "G";
      case "DOP": return "RD$";
      case "GBP": return "£";
      case "CAD": return "CA$";
      case "CHF": return "Fr";
      case "AUD": return "A$";
      case "JPY": return "¥";
      case "CNY": return "¥";
      case "RUB": return "₽";
      case "BRL": return "R$";
      case "MXN": return "Mex$";
      case "INR": return "₹";
      case "MAD": return "DH";
      case "XAF": return "FCFA";
      case "XOF": return "CFA";
      case "NGN": return "₦";
      case "ZAR": return "R";
      case "KES": return "KSh";
      default: return currencyCode;
    }
  };

  // Format price with the correct currency
  const formattedPrice = `${product.price.toFixed(2)} ${getCurrencySymbol(product.currency)}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-start justify-between">
          <span>{product.title}</span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(product)}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(product)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div 
          className="relative aspect-video w-full overflow-hidden rounded-lg cursor-pointer" 
          onClick={handleViewProduct}
        >
          {product.photo_url ? (
            <OptimizedImage
              src={product.photo_url}
              alt={product.title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={() => console.error(`Erreur de chargement de l'image: ${product.photo_url}`)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
              <span>Aucune image</span>
            </div>
          )}
        </div>
        
        <p className="text-sm text-muted-foreground">
          {product.description || "Aucune description"}
        </p>
        <div className="flex justify-between text-sm">
          <span>{formattedPrice}</span>
          <span>État: {formatCondition(product.condition)}</span>
        </div>

        {product.quotes && product.quotes.length > 0 && (
          <div className="text-sm">
            <p className="font-medium mb-1">Devis disponibles:</p>
            <div className="flex flex-wrap gap-1">
              {product.quotes.map(quote => {
                // Format each quote price with the product's currency
                const quotePrice = `${quote.price.toFixed(2)} ${getCurrencySymbol(product.currency)}`;
                
                return (
                  <span key={quote.id} className="px-2 py-1 bg-gray-100 rounded-sm text-xs">
                    {quote.name}: {quotePrice}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-2 text-sm text-muted-foreground">
          {product.address && (
            <button
              onClick={handleAddressClick}
              className="flex items-center gap-2 hover:text-primary"
            >
              <MapPin className="h-4 w-4" />
              <span>{product.address}</span>
            </button>
          )}
          {product.phone && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              <a href={`tel:${product.phone}`} className="hover:text-primary">
                {product.phone}
              </a>
            </div>
          )}
        </div>
        
        {/* Add View Product button */}
        <Button 
          variant="outline" 
          className="w-full mt-2 border-primary text-primary hover:bg-primary/5"
          onClick={handleViewProduct}
        >
          <Eye className="mr-2 h-4 w-4" /> Voir le produit
        </Button>
      </CardContent>
    </Card>
  );
};
